import { z } from 'zod';
import { apiFor } from '@/api/client';

/*
 * Runtime contract for the "Système U — Minions — Pinball" dashboard payload
 * (program su04, "Mission Incognito"). THIS SCHEMA IS THE PIPELINE'S CONTRACT:
 * client-portal/pipelines/systeme-u-minions-2026 emits exactly this shape.
 *
 * Conventions (as for the other Système U and Morrisons dashboards):
 *   - every figure is a RAW NUMBER (counts, seconds); the UI formats them and
 *     derives the rates (games per player, stickiness, retention …);
 *   - a figure the source cannot give is `null` and renders "—" — never 0.
 *     The app-only figures (invites sent, time in the app) are null when the
 *     app sent no events;
 *   - a game is a game played = its Winning Engine draw (`transaction`
 *     WIN_ENG): the free daily game and the bonus games alike, each earning one
 *     lottery ticket (BE review, 3 Nov — `spend_game` holds the free game only);
 *   - a player is a member with at least one game; active on a day / in a week
 *     = at least one game that day / week;
 *   - local days in Europe/Paris; weeks run Tuesday → Monday from go-live
 *     (Tue 3 Nov 2026), week 5 is the six days 1–6 Dec.
 */

const n = z.number();
const nn = z.number().nullable();

/** One campaign day — the daily chart and its table. All three are null for a day still to come. */
const daySchema = z.object({
  /** ISO date, YYYY-MM-DD. */
  date: z.string(),
  /** Members who opened the game that day (a visitor back on another day counts again). */
  visitors: nn,
  /** Players with at least one game that day (DAU). */
  activePlayers: nn,
  plays: nn,
});

const weekSchema = z.object({
  week: z.number().int().positive(),
  start: z.string(),
  end: z.string(),
  status: z.enum(['done', 'live', 'planned']),
  /** Players with at least one game that week (counted per week). Every figure is null for a week to come. */
  uniquePlayers: nn,
  /** Players whose FIRST game falls in that week. */
  newPlayers: nn,
  plays: nn,
  /** One per game. */
  tickets: nn,
  /** Login missions completed that week. */
  loginMissions: nn,
  /** Invites sent from the app — app events. */
  invitesSent: nn,
  /** Time in the app that week, all sessions — app events. */
  timeSec: nn,
});

/** Players by the number of games they played over the campaign. */
const playsBucketSchema = z.object({
  /** '1', '2-3', '4-7', '8-14', '15+'. */
  bucket: z.string(),
  min: z.number().int().positive(),
  /** Null for the open-ended last bucket. */
  max: z.number().int().positive().nullable(),
  players: n,
});

/** Day-N retention: of the players whose first game day + N has passed, how many played again on that day. */
const retentionSchema = z.object({ cohort: n, returned: n });

export const minionsPayloadSchema = z.object({
  campaign: z.object({
    name: z.string(),
    start: z.string(),
    end: z.string(),
    timezone: z.string(),
    live: z.boolean(),
    /** Campaign week running now (1-based); 0 before go-live. */
    currentWeek: z.number().int().min(0),
    totalWeeks: z.number().int().positive(),
    /** Free games a day (period `games_limit`). */
    dailyGames: z.number().int().positive(),
    /** Redemptions of one invite code that earn the inviter a game (setting `referralUsesPerCode`). */
    inviteCap: z.number().int().positive(),
  }),
  totals: z.object({
    uniqueVisitors: n,
    optIns: n,
    uniquePlayers: n,
    plays: n,
    tickets: n,
    /** Time in the app, all sessions — app events. */
    totalTimeSec: nn,
    sessions: nn,
    /** Average daily active players over the elapsed days. */
    dauAvg: nn,
    /** Average daily active players over the last 7 elapsed days. */
    dauLast7: nn,
    /** Players in the last completed week; null until week 1 is over. */
    wauLastFullWeek: nn,
    /** Average daily active players in that same week (÷ WAU = stickiness). */
    dauLastFullWeek: nn,
    d1: retentionSchema,
    d7: retentionSchema,
    /** Players who played on more than one day. */
    recurringPlayers: n,
    loginMission: z.object({ completed: n, extraGames: n }),
    invites: z.object({
      /** Invites sent from the app — app events. */
      sent: nn,
      /** Players who sent at least one invite — app events. */
      inviters: nn,
      /** Players who joined with a code in the window and played at least one game. */
      inviteesPlayed: n,
      /** Codes redeemed in the window (backend). */
      redeemed: n,
      /** Extra games granted by the referral missions (inviter and invitee). */
      extraGames: n,
      /** Players whose code was redeemed `inviteCap` times. */
      limitReached: n,
    }),
    /** Members whose latest confirmed answer is "yes" — on either channel / on e-mail / on SMS. */
    consents: z.object({ any: n, email: n, sms: n }),
  }),
  days: z.array(daySchema),
  weeks: z.array(weekSchema),
  playsDistribution: z.array(playsBucketSchema),
});

export const minionsEnvelopeSchema = z.object({
  data: minionsPayloadSchema,
  meta: z.object({ last_refreshed_at: z.string().nullable() }).partial().optional(),
});

export type MinionsPayload = z.infer<typeof minionsPayloadSchema>;
export type MinionsDay = z.infer<typeof daySchema>;
export type MinionsWeek = z.infer<typeof weekSchema>;
export type MinionsPlaysBucket = z.infer<typeof playsBucketSchema>;
export type MinionsEnvelope = z.infer<typeof minionsEnvelopeSchema>;

/** Same generic report endpoint every dashboard uses: GET <base>/reports/<id>. */
export async function getMinionsReport(reportId: string, baseUrl: string): Promise<MinionsEnvelope> {
  const raw = await apiFor(baseUrl).get(`reports/${reportId}`).json();
  return minionsEnvelopeSchema.parse(raw);
}
